import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, Phone, MapPin } from "lucide-react";
import { CONTACT, waHref, ASSETS } from "@/lib/contact";

// Masked line-by-line reveal used for H1
const lineParent = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};
const lineChild = {
  hidden: { y: "115%" },
  show: {
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-brand-black pt-24 sm:pt-28"
    >
      {/* subtle grid overlay */}
      <motion.div
        aria-hidden="true"
        style={{ y: gridY }}
        className="pointer-events-none absolute inset-0 opacity-[0.09]"
      >
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              "linear-gradient(rgba(192,192,192,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(192,192,192,0.5) 1px, transparent 1px)",
            backgroundSize: "88px 88px",
          }}
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 pb-16 sm:px-8 sm:pb-24">
        {/* status badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="flex flex-wrap items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.4em] text-neutral-400 sm:text-xs"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span data-testid="hero-status">ONLINE AGORA · 24/7</span>
          <span className="hidden text-chrome/50 sm:inline">·</span>
          <span className="hidden items-center gap-1 text-chrome/70 sm:inline-flex">
            <MapPin className="h-3 w-3" /> BELÉM · ANANINDEUA · MARITUBA
          </span>
        </motion.div>

        {/* Two-column: content + logo showcase */}
        <div className="mt-8 grid grid-cols-12 items-center gap-4 sm:mt-12 sm:gap-8 lg:gap-12">
          {/* LEFT — title + description */}
          <div className="col-span-7 lg:col-span-7">
            <motion.h1
              data-testid="hero-h1"
              variants={lineParent}
              initial="hidden"
              animate="show"
              className="font-display leading-[0.85] tracking-[-0.02em]"
            >
              {[
                "GUINCHO",
                "E REBOQUE",
                <span key="s" className="text-brand-yellow">
                  24 HORAS
                </span>,
              ].map((word, i) => (
                <span
                  key={i}
                  className="block overflow-hidden py-[0.02em] text-[8.5vw] text-white sm:text-[9vw] lg:text-[7vw]"
                >
                  <motion.span variants={lineChild} className="inline-block">
                    {word}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-5 max-w-[520px] font-body text-sm leading-relaxed text-neutral-300 sm:mt-8 sm:text-lg"
            >
              Atendimento <span className="text-brand-yellow">rápido, seguro</span>{" "}
              e com <span className="text-brand-yellow">preço justo</span>.
              Veículos leves, utilitários e motocicletas em{" "}
              <span className="text-white">Belém, Ananindeua, Marituba</span> e
              toda a Região Metropolitana.
            </motion.p>
          </div>

          {/* RIGHT — Logo showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: logoY }}
            className="col-span-5 lg:col-span-5"
          >
            <div className="relative mx-auto aspect-square w-full max-w-[520px] lg:max-w-none">
              {/* glow */}
              <div
                aria-hidden="true"
                className="absolute inset-0 -z-10 rounded-full bg-brand-yellow/25 blur-[110px]"
              />
              {/* rotating chrome ring */}
              <motion.div
                aria-hidden="true"
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-dashed border-chrome/25"
              />
              {/* frame */}
              <div className="absolute inset-4 overflow-hidden border border-chrome/25 bg-black/40 backdrop-blur-[2px] sm:inset-6">
                <motion.img
                  src={ASSETS.logo}
                  alt="GORILLAS REBOQUE - Guincho 24h Belém"
                  className="h-full w-full object-cover"
                  loading="eager"
                  initial={{ scale: 1.08 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                />
                {/* frame corners */}
                <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-brand-yellow" />
                <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r-2 border-t-2 border-brand-yellow" />
                <span className="pointer-events-none absolute left-3 bottom-3 h-4 w-4 border-l-2 border-b-2 border-brand-yellow" />
                <span className="pointer-events-none absolute right-3 bottom-3 h-4 w-4 border-r-2 border-b-2 border-brand-yellow" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTAs — full width below hero row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-start sm:gap-4"
        >
          <a
            data-testid="hero-whatsapp-btn"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center justify-center gap-3 overflow-hidden bg-brand-green px-6 py-4 font-display text-base tracking-widest text-white transition-transform active:scale-[0.97] sm:text-lg"
          >
            <span className="absolute inset-0 -translate-x-full bg-black transition-transform duration-500 group-hover:translate-x-0" />
            <MessageCircle
              className="relative h-5 w-5 transition-colors group-hover:text-brand-green"
              strokeWidth={2.5}
            />
            <span className="relative transition-colors group-hover:text-brand-green">
              CHAMAR NO WHATSAPP
            </span>
          </a>
          <a
            data-testid="hero-call-btn"
            href={CONTACT.telHref}
            className="group relative inline-flex items-center justify-center gap-3 overflow-hidden bg-brand-yellow px-6 py-4 font-display text-base tracking-widest text-black transition-transform active:scale-[0.97] sm:text-lg"
          >
            <span className="absolute inset-0 -translate-x-full bg-black transition-transform duration-500 group-hover:translate-x-0" />
            <Phone
              className="relative h-5 w-5 transition-colors group-hover:text-brand-yellow"
              strokeWidth={2.5}
            />
            <span className="relative transition-colors group-hover:text-brand-yellow">
              LIGAR
            </span>
          </a>
        </motion.div>

        {/* stat row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-chrome/15 pt-8 sm:mt-20 sm:grid-cols-4"
        >
          {[
            ["24/7", "Plantão ativo"],
            ["15min*", "Resposta média"],
            ["3 cidades", "Cobertura direta"],
            ["Pix · Cartão", "Pagamento fácil"],
          ].map(([big, small]) => (
            <div key={big} className="flex flex-col">
              <span className="font-display text-4xl tracking-tight text-white sm:text-5xl">
                {big}
              </span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-500">
                {small}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* bottom hazard tape */}
      <div
        aria-hidden="true"
        className="relative z-10 h-3 w-full"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #fdb704 0 22px, #050505 22px 44px)",
        }}
      />
    </section>
  );
}
